import { PhaseTypeEnum } from '@vatos-pas/common';

/**
 * Skipped phase display value - shown for all fields of phases that don't exist
 */
export const SKIPPED_PHASE_DISPLAY = '-';

/**
 * Page size for the Contractors available to a phase in a region. The
 * largest list on 2026-10-29 was 110 (Orlando Finishing). The limit stays
 * bounded because every row carries eager-joined regions and users, and the
 * capacity lookup sends every listed id in one URL.
 */
export const PHASE_CONTRACTOR_LIST_LIMIT = 200;

/**
 * Safely get a phase from jobPhases array
 * Returns null if phase doesn't exist (was skipped)
 */
export const getPhaseByType = (
  jobPhases: any[] | undefined | null,
  phaseType: PhaseTypeEnum,
): any => {
  if (!jobPhases || !Array.isArray(jobPhases)) return null;
  return jobPhases.find(jp => jp.phaseType === phaseType) || null;
};

/**
 * Check if a phase was skipped (doesn't exist because job started after it)
 */
export const isSkippedPhase = (
  jobPhases: any[] | undefined | null,
  phaseType: PhaseTypeEnum,
): boolean => {
  return getPhaseByType(jobPhases, phaseType) === null;
};

/**
 * Check if a phase is the auto-approved starting phase (no earlier phases exist).
 * Used to show '-' in BumpOut rows when the job was created at this phase.
 *
 * Excludes phases that went through the normal workflow (have requested or
 * contractorId set), which indicates the phase was actively processed rather
 * than auto-approved at job creation.
 */
export const isStartingApprovedPhase = (phaseJob: any, theJob: any): boolean =>
  phaseJob.approved &&
  !phaseJob.requested &&
  !phaseJob.contractorId &&
  phaseJob.priority === theJob.jobPhase?.priority &&
  !theJob.jobPhases?.some((jp: any) => jp.priority < phaseJob.priority);

/**
 * PhaseTypeEnum → priority for Drywall workflow.
 * Source: phase.seeder.ts
 */
export const DRYWALL_PHASE_TYPE_TO_PRIORITY: Record<string, number> = {
  [PhaseTypeEnum.Materials]: 100,
  [PhaseTypeEnum.Garage]: 200,
  [PhaseTypeEnum.Hanging]: 300,
  [PhaseTypeEnum.Finishing]: 400,
  [PhaseTypeEnum.Spraying]: 500,
  [PhaseTypeEnum.Bump1]: 600,
  [PhaseTypeEnum.Bump2]: 700,
  [PhaseTypeEnum.Bump3]: 800,
  [PhaseTypeEnum.Bump4]: 900,
  [PhaseTypeEnum.Bump5]: 1000,
};

/**
 * PhaseTypeEnum → priority for DrywallAndPaint workflow.
 */
export const DRYWALL_AND_PAINT_PHASE_TYPE_TO_PRIORITY: Record<string, number> =
  {
    [PhaseTypeEnum.Materials]: 100,
    [PhaseTypeEnum.Garage]: 200,
    [PhaseTypeEnum.Hang]: 300,
    [PhaseTypeEnum.Finish]: 400,
    [PhaseTypeEnum.Prime]: 500,
    [PhaseTypeEnum.Trim1]: 600,
    [PhaseTypeEnum.ExteriorOne]: 650,
    [PhaseTypeEnum.DrywallTouchUp1]: 700,
    [PhaseTypeEnum.InteriorPaint]: 800,
    [PhaseTypeEnum.Trim2]: 900,
    [PhaseTypeEnum.DrywallTouchUp2]: 1000,
    [PhaseTypeEnum.FinalPaint]: 1100,
    [PhaseTypeEnum.DrywallTouchUp3]: 1200,
    [PhaseTypeEnum.CMBluetape]: 1300,
    [PhaseTypeEnum.FinalBluetape]: 1400,
    [PhaseTypeEnum.ExteriorTwo]: 1500,
  };

/**
 * Combined PhaseTypeEnum → priority map for all workflows.
 * No collisions — Drywall and DrywallAndPaint use different phase types
 * for the same priority slots (e.g. "Hanging" vs "Hang").
 * Materials (100) and Garage (200) overlap safely — identical priorities.
 */
export const PHASE_TYPE_TO_PRIORITY: Record<string, number> = {
  ...DRYWALL_PHASE_TYPE_TO_PRIORITY,
  ...DRYWALL_AND_PAINT_PHASE_TYPE_TO_PRIORITY,
};

/**
 * Check if a phase column is the auto-approved starting phase in a list view.
 *
 * True when the job IS at this phase (currentPriority === columnPriority)
 * but the phase was auto-approved at creation (no contractor, no suggested, no date).
 * If currentPhaseApproved is false, the phase is actively being worked — not auto-approved.
 */
export const isStartingPhaseColumn = (
  currentPhaseType: string | undefined | null,
  columnPhasePriority: number,
  contractorName: string | null | undefined,
  suggestedContractorName: string | null | undefined,
  dateStart: any,
  currentPhaseApproved?: boolean,
): boolean => {
  if (!currentPhaseType) return false;
  if (currentPhaseApproved === false) return false;
  const currentPriority = PHASE_TYPE_TO_PRIORITY[currentPhaseType] ?? 0;
  if (currentPriority !== columnPhasePriority) return false;
  return !contractorName && !suggestedContractorName && !dateStart;
};

/**
 * Check if a phase column is "skipped" in a list view record.
 *
 * A phase is skipped when:
 * 1. The job's current phase priority is HIGHER than the column's phase priority
 * 2. AND the column has no data (no contractor, no suggested contractor, no date)
 */
export const isPhaseColumnSkipped = (
  currentPhaseType: string | undefined | null,
  columnPhasePriority: number,
  contractorName: string | null | undefined,
  suggestedContractorName: string | null | undefined,
  dateStart: any,
): boolean => {
  if (!currentPhaseType) return false;
  const currentPriority = PHASE_TYPE_TO_PRIORITY[currentPhaseType] ?? 0;
  if (currentPriority <= columnPhasePriority) return false;
  return !contractorName && !suggestedContractorName && !dateStart;
};
